import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import {
  AreaCode,
  ForceCode,
  getUnitLabelForUnitSystem,
  LengthCode,
  TemperatureCode,
  LinearFrequencyCode,
  UnitSystem as IcommerceUnitSystem,
} from '@icommerce/units';
import type { CurrentLifeTimeConfiguration } from '@hub/free-form-hub/common/lifetime/useLifetime';
import type { LifetimeLoadByForceAndArea } from '@igus/tribology-domain/src/lib/lifetime/lifetime.definitions';
import { type OppositeMaterialKeyType, OppositeMaterialMap } from '@hub/free-form-hub/common/lifetime/lifeTime.types';
import { UnitSystem, fromMetricAreaMilli, fromMetricDistanceMilli, fromMetricTemperature } from '@igus/kopla-domain';

// Convert UnitSystem from @igus/kopla-domain to @icommerce/units format
const convertToIcommerceUnitSystem = (unitSystem: UnitSystem): IcommerceUnitSystem => {
  return unitSystem === UnitSystem.metric ? IcommerceUnitSystem.metric : IcommerceUnitSystem.imperial;
};

interface LifeTimePopUpInfoProps {
  activeModal: boolean;
  setActiveModal: (value: boolean) => void;
  unitSystem: UnitSystem;
  currentLifeTimeConfiguration: CurrentLifeTimeConfiguration;
}

export const LifeTimePopUpInfo = ({
  activeModal,
  currentLifeTimeConfiguration,
  setActiveModal,
  unitSystem,
}: LifeTimePopUpInfoProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const router = useRouter();
  const locale = getLocale(router);

  const defaultLifeTimeData = [
    {
      key: t(`SECTION_MATERIALS`, { ns: 'free-form-hub/lifeTime' }),
      value: OppositeMaterialMap[currentLifeTimeConfiguration?.oppositeMaterialId as OppositeMaterialKeyType],
      unit: '',
    },
    {
      key: t(`ENVIRONMENT`, { ns: 'free-form-hub/lifeTime' }),
      value: currentLifeTimeConfiguration?.environment,
      unit: '',
    },
    {
      key: t(`SURFACE_AREA`, { ns: 'free-form-hub/lifeTime' }),
      value: fromMetricAreaMilli(unitSystem, (currentLifeTimeConfiguration?.load as LifetimeLoadByForceAndArea)?.area),
      unit: getUnitLabelForUnitSystem(AreaCode.SQUARE_MILLIMETER, convertToIcommerceUnitSystem(unitSystem)),
    },
    {
      key: t(`LOAD`, { ns: 'free-form-hub/lifeTime' }),
      value: (currentLifeTimeConfiguration?.load as LifetimeLoadByForceAndArea)?.force,
      unit: getUnitLabelForUnitSystem(ForceCode.NEWTON, convertToIcommerceUnitSystem(unitSystem)),
    },
    {
      key: t(`TEMPERATURE`, { ns: 'free-form-hub/lifeTime' }),
      value: fromMetricTemperature(unitSystem, currentLifeTimeConfiguration?.temperature),
      unit: getUnitLabelForUnitSystem(TemperatureCode.CELSIUS, convertToIcommerceUnitSystem(unitSystem)),
    },
    {
      key: t(`ACCEPTABLE_WEAR`, { ns: 'free-form-hub/lifeTime' }),
      value: fromMetricDistanceMilli(unitSystem, currentLifeTimeConfiguration?.acceptableWear),
      unit: getUnitLabelForUnitSystem(LengthCode.MILLIMETER, convertToIcommerceUnitSystem(unitSystem)),
    },
    {
      key: t(`ACTIVITY_MINUTES_PER_HOUR`, { ns: 'free-form-hub/lifeTime' }),
      value: currentLifeTimeConfiguration?.activityMinutesPerHour,
      unit: 'min',
    },
    {
      key: t(`TYPE_OF_MOVEMENT`, { ns: 'free-form-hub/lifeTime' }),
      value: t(`${currentLifeTimeConfiguration?.typeOfMovement.toUpperCase()}`, { ns: 'free-form-hub/lifeTime' }),
      unit: '',
    },
  ];

  const getLifetimeData = () => [
    ...defaultLifeTimeData,
    {
      key: t(`LINEAR_FREQUENCY`, { ns: 'free-form-hub/lifeTime' }),
      value: currentLifeTimeConfiguration?.linearFrequency,
      unit: getUnitLabelForUnitSystem(
        LinearFrequencyCode.REVOLUTION_PER_MINUTE,
        convertToIcommerceUnitSystem(unitSystem)
      ),
    },
    {
      key: t(`LINEAR_STROKE_LENGTH`, { ns: 'free-form-hub/lifeTime' }),
      value: fromMetricDistanceMilli(unitSystem, currentLifeTimeConfiguration?.linearStrokeLength),
      unit: getUnitLabelForUnitSystem(LengthCode.MILLIMETER, convertToIcommerceUnitSystem(unitSystem)),
    },
  ];

  return (
    <Modal
      variant={ModalVariant.DEFAULT}
      visible={activeModal}
      onClose={() => setActiveModal(false)}
      headline={t(`HEADLINES.LIFETIME`, { ns: 'free-form-hub/freeFormHub' })}
    >
      <ul data-testid="life-time-modal-content">
        {getLifetimeData().map((item, index) => (
          <li key={item.key} data-testid={`POP_UP_INFO_${item.key}`}>
            <span className="font-bold">{item.key}: </span>
            <span>
              {index === 0
                ? t(`SHAFT_MATERIAL.${item.value}`, { ns: 'free-form-hub/lifeTime' })
                : item.value?.toLocaleString(locale)}{' '}
              {item.unit !== '' && item.unit}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
};
